import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, LockKeyhole } from 'lucide-react';
import { authService } from '../services/authService';
import { notify } from '../utils/notifications';
import PasswordRequirements from '../components/PasswordRequirements';
import PolicyConfirmations from '../components/PolicyConfirmations';
import type { PolicyConfirmationsValue } from '../services/authService';
import { passwordPolicyError } from '../utils/passwordPolicy';

export default function ActivateAccountScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [policyConfirmations, setPolicyConfirmations] = useState<PolicyConfirmationsValue>({
    adultAttested: false,
    termsAccepted: false,
  });

  async function activate() {
    if (!token) return;
    const passwordError = passwordPolicyError(password);
    if (passwordError) { notify.error(passwordError); return; }
    if (password !== confirmation) { notify.error('Passwords do not match'); return; }
    if (!policyConfirmations.adultAttested || !policyConfirmations.termsAccepted) {
      notify.error('Please confirm your age and accept the Terms of Service');
      return;
    }
    setSubmitting(true);
    try {
      await authService.activateImportedAccount(token, password, policyConfirmations);
      notify.success('Account activated', 'Sign in with your new password.');
      navigate('/signin', { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      notify.error('Could not activate account', message && message !== '[object Object]' ? message : 'This link may have expired. Contact the event organizer for a new link.');
    } finally { setSubmitting(false); }
  }

  return <div className="size-full flex flex-col overflow-y-auto px-6" style={{ backgroundColor: 'var(--app-bg)' }}>
    <button type="button" aria-label="Back to sign in" onClick={() => navigate('/signin')} className="mt-6 -ml-2 size-11 rounded-full flex items-center justify-center active:opacity-70" style={{ color: 'var(--app-text)' }}><ArrowLeft className="w-6 h-6" /></button>
    <div className="flex-1 flex flex-col justify-center pb-14">
      <div className="rounded-2xl size-16 flex items-center justify-center mb-6 border" style={{ backgroundColor: 'var(--app-accent-dim)', borderColor: 'var(--app-accent-border)' }}><LockKeyhole className="w-8 h-8" style={{ color: 'var(--app-accent)' }} /></div>
      <h1 className="text-[30px] tracking-tight" style={{ color: 'var(--app-text)' }}>Activate your account</h1>
      <p className="text-base mt-3 leading-6" style={{ color: 'var(--app-text-muted)' }}>Set your password to join your conference in the app.</p>
      {!token ? <div className="mt-8 rounded-xl p-4 border" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}><p style={{ color: 'var(--app-text)' }}>This activation link is incomplete or invalid.</p><button onClick={() => navigate('/signin')} className="mt-5 h-12 w-full rounded-xl text-white" style={{ backgroundColor: 'var(--app-accent-solid)' }}>Back to sign in</button></div> : <div className="mt-8 flex flex-col gap-4">
        <PasswordField id="new-password" label="New password" value={password} onChange={setPassword} />
        <PasswordRequirements password={password} />
        <PasswordField id="confirm-password" label="Confirm password" value={confirmation} onChange={setConfirmation} />
        <PolicyConfirmations
          value={policyConfirmations}
          onChange={setPolicyConfirmations}
          disabled={submitting}
        />
        <button onClick={activate} disabled={submitting || !policyConfirmations.adultAttested || !policyConfirmations.termsAccepted} className="mt-2 h-14 rounded-xl text-white text-lg disabled:opacity-70 active:scale-[0.98]" style={{ backgroundColor: 'var(--app-accent-solid)' }}>{submitting ? 'Activating…' : 'Activate account'}</button>
      </div>}
    </div>
  </div>;
}

function PasswordField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (value: string) => void }) {
  return <div className="flex flex-col gap-2"><label htmlFor={id} className="text-base" style={{ color: 'var(--app-text)' }}>{label}</label><input id={id} type="password" autoComplete="new-password" value={value} onChange={(event) => onChange(event.target.value)} className="h-14 rounded-xl px-4 text-base outline-none" style={{ backgroundColor: 'var(--app-input-bg)', border: '1px solid var(--app-input-border)', color: 'var(--app-text)' }} /></div>;
}
